import type { Dispatch, SetStateAction } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Box, Stack, Typography } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import * as L from "leaflet"
import { spline } from "leaflet-spline"
import { Marker, useMap } from "react-leaflet"

import { useLineClamp } from "@ggs/hooks"
import { Map } from "@ggs/ui/local"
import { Container, Link, SvgIcon, Tags, WYSIWYG, type FC, type FCWC, type iconKeys } from ".."

type MapOverviewMarker = {
  label: string
  lat: number
  lng: number
  uri: string
}

type TripMapOverviewProps = {
  id?: string
  title: string
  subtitle?: string
  description: string
  special?: {
    description: string
    savings?: string
    addedValue?: boolean
    shortDescription?: string
  }
  items: MapOverviewMarker[]
  tripCategories: { label: string; uri: string }[]
  deadline?: string | null
}

const TripMapOverview: FC<TripMapOverviewProps> = ({
  id = "overview",
  title,
  subtitle,
  description,
  special,
  items,
  deadline,
  tripCategories,
}) => {
  const isItemsAvailable = items && items.length > 0

  return (
    <Container id={id} maxWidth={1360} py={{ xs: 10, lg: 26.5 }}>
      {subtitle && (
        <Typography variant="body.lg" component="h3" sx={{ mb: { xs: 5, lg: 10 } }}>
          {subtitle}
        </Typography>
      )}
      <TripOverviewSection>
        <TripOverviewContent>
          <Typography variant="heading.md" component="h2" sx={{ fontSize: { xs: 18, md: 32 } }}>
            {title}
          </Typography>
          {tripCategories?.length > 0 && (
            <Tags tags={tripCategories} pt={{ xs: 5, md: 10 }} pb={{ xs: 2, md: 6 }} autoWidth />
          )}
          {special?.description && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: "18px",
                background: "#FEF6F4",
                border: "1px solid #FDEEE9",
                borderRadius: "4px",
                mt: { xs: 3, md: 4 },
                mb: { xs: 5, md: 7 },
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "180%",
                  color: "#464446",
                  mb: 1,
                  mt: 2,
                }}
              >
                Special Offer Details
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontStyle: "normal",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "180%",
                  color: "#464446",
                }}
                dangerouslySetInnerHTML={{ __html: special.description }}
              />
            </Box>
          )}
          <TripOverviewDescription {...{ title, description }} />
        </TripOverviewContent>
        {isItemsAvailable && <TripMap items={items} />}
      </TripOverviewSection>
    </Container>
  )
}

export default TripMapOverview

const TripOverviewSection: FCWC = ({ children }) => (
  <Stack
    direction={{ xs: "column", lg: "row" }}
    justifyContent={{ lg: "space-between" }}
    spacing={{ xs: 20, lg: 30 }}
    mb={{ xs: 30, lg: "unset" }}
    position="relative"
  >
    {children}
  </Stack>
)

const TripOverviewContent: FCWC = ({ children }) => (
  <Box
    flex="1"
    maxWidth={{ lg: 700 }}
    position={{ lg: "sticky" }}
    top={{ lg: 100 }}
    height="fit-content"
  >
    {children}
  </Box>
)

const TripOverviewDescription: FC<Pick<TripMapOverviewProps, "title" | "description">> = ({
  title,
  description,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { isClamped, isShowingMore, toggleIsShowingMore } = useLineClamp({ ref, titleRef: null })

  // Extract first paragraph from HTML content
  const firstParagraph = description.match(/<p[^>]*>(.*?)<\/p>/)?.[1] || ""
  const remainingContent = description.replace(/<p[^>]*>(.*?)<\/p>/, "").trim()

  return (
    <>
      {firstParagraph && (
        <Typography
          variant="heading.md"
          dangerouslySetInnerHTML={{ __html: firstParagraph }}
          sx={{
            fontSize: "24px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "144%",
            letterSpacing: "-0.192px",
          }}
        />
      )}
      {remainingContent && (
        <>
          <WYSIWYG
            ref={ref}
            content={remainingContent}
            sx={{
              ...(!isShowingMore && {
                "mt": { xs: 5, md: 7 },
                "overflow": "hidden",
                "display": "-webkit-box",
                "WebkitBoxOrient": "vertical",
                "WebkitLineClamp": 4,

                "& > *": {
                  display: "inline",
                },
              }),
            }}
          />
          {isClamped && (
            <Box sx={{ mt: { xs: 5, lg: 10 } }}>
              <Link
                variant="link.md"
                onClick={toggleIsShowingMore}
                aria-label={`Read more about ${title}`}
              >
                {isShowingMore ? "Read Less" : "Read More"}
              </Link>
            </Box>
          )}
        </>
      )}
    </>
  )
}

const TripMap: FC<Pick<TripMapOverviewProps, "items">> = ({ items }) => {
  const [map, setMap] = useState<L.Map | null>(null)
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  const [labelIcon, setLabelIcon] = useState<string | null>(null)

  useEffect(() => {
    if (map) {
      const coords = L.latLngBounds(items.map((item) => L.latLng(item.lat, item.lng)))
      // Disable scroll wheel zoom only on mobile devices
      if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        map.scrollWheelZoom.disable()
      }
      map?.panTo(coords.getCenter())
      map.fitBounds(coords, { padding: [50, 50], maxZoom: 14 })
    }
  }, [map])

  map?.zoomControl.setPosition("topright")

  return (
    <Box
      flex={{ xs: "1", lg: "1.5 0 auto" }}
      borderRadius={1.5}
      position="relative"
      maxWidth={{ lg: 520 }}
      px={{ xs: 2, lg: 0 }}
      maxHeight={764}
      width={{ xs: "100%", lg: "auto" }}
      sx={{
        "aspectRatio": { xs: "1", lg: "520/764" },
        ".leaflet-popup": {
          mb: 10,
        },
        ".leaflet-container": {
          borderRadius: 1.5,
        },
        ".leaflet-popup-content-wrapper": {
          boxShadow: (theme) => `2px 2px 20px ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
          borderRadius: 1.5,
          overflow: "hidden",
        },

        ".leaflet-popup-content": {
          margin: 0,
        },

        ".leaflet-popup-tip-container": {
          display: "none",
        },

        ".leaflet-marker-icon": {
          bgcolor: "transparent",
          border: "none",
        },

        ".leaflet-control-zoom": {
          border: "0!important",
          display: "flex",
          flexDirection: "column",

          rowGap: 1.5,
        },

        ".leaflet-control-zoom-in, .leaflet-control-zoom-out": {
          borderRadius: "9999px!important",
          border: "0!important",
          boxShadow: "1px 1px 2px 0px rgba(48, 67, 105, 0.10)!important",
          padding: "0!important",
          fontWeight: "400!important",
          fontSize: "16px!important",
          lineHeight: "16px!important",
          width: "30px!important",
          height: "30px!important",
          position: "relative",

          span: {
            display: "none",
          },
        },

        ".leaflet-control-zoom-in::before": {
          background:
            "url(\"data:image/svg+xml,%3Csvg width='11' height='10' viewBox='0 0 11 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M6 0V10H5V0H6Z' fill='%23464446'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M10.5 5.5H0.5V4.5H10.5V5.5Z' fill='%23464446'/%3E%3C/svg%3E%0A\")",
          content: "''",
          display: "block",
          width: 11,
          height: 10,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        },

        ".leaflet-control-zoom-out::before": {
          background:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 11 2' fill='%23464446' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M10.5 1.5H0.5V0.5H10.5V1.5Z' /%3E%3C/svg%3E%0A\")",
          content: "''",
          width: 11,
          height: 2,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        },

        ".leaflet-container .leaflet-control-attribution": {
          display: "none",
        },
      }}
    >
      <Map setMap={setMap}>
        <MapMarkers markers={items} setActiveLabel={setActiveLabel} setLabelIcon={setLabelIcon} />
        <MapCurve markers={items} />
        {activeLabel && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            borderRadius={1.5}
            bgcolor={(theme) => theme.palette["neutral.100"].main}
            zIndex={9000}
            position="absolute"
            bottom={30}
            left={10}
            right={10}
            px={5}
            py={3}
          >
            <SvgIcon
              viewBox="0 0 14 14"
              size={14}
              variant={labelIcon as iconKeys}
              sx={{ fill: (theme) => theme.palette["primary.800"].main }}
            />
            <Typography variant="body.sm" fontWeight="500">
              {activeLabel}
            </Typography>
          </Stack>
        )}
      </Map>
      <MapLegend />
    </Box>
  )
}

const MapMarkers: FC<{
  markers: MapOverviewMarker[]
  setActiveLabel: Dispatch<SetStateAction<string | null>>
  setLabelIcon: Dispatch<SetStateAction<string | null>>
}> = ({ markers, setActiveLabel, setLabelIcon }) => {
  const map = useMap()
  const [active, setActive] = useState(-1)

  const markerIconActive = useCallback((index: number, length: number) => {
    if (typeof window !== "undefined") {
      if (length === 1) {
        return new L.Icon({
          iconUrl: "/images/trip-map-start-marker-active.svg",
          iconSize: new L.Point(55, 55),
        })
      } else if (index === 0) {
        return new L.Icon({
          iconUrl: "/images/trip-map-start-marker-active.svg",
          iconSize: new L.Point(55, 55),
        })
      } else if (index === length - 1) {
        return new L.Icon({
          iconUrl: "/images/trip-map-end-marker-active.svg",
          iconSize: new L.Point(55, 55),
        })
      } else {
        return new L.Icon({
          iconUrl: "/images/trip-map-marker-active.svg",
          iconSize: new L.Point(30, 30),
        })
      }
    }
  }, [])

  const markerIcon = useCallback((index: number, length: number) => {
    if (typeof window !== "undefined") {
      if (length === 1) {
        return new L.Icon({
          iconUrl: "/images/trip-map-start-marker.svg",
          iconSize: new L.Point(50, 50),
        })
      } else if (index === 0) {
        return new L.Icon({
          iconUrl: "/images/trip-map-start-marker.svg",
          iconSize: new L.Point(50, 50),
        })
      } else if (index === length - 1) {
        return new L.Icon({
          iconUrl: "/images/trip-map-end-marker.svg",
          iconSize: new L.Point(50, 50),
        })
      } else {
        return new L.Icon({
          iconUrl: "/images/trip-map-marker.svg",
          iconSize: new L.Point(15, 15),
        })
      }
    }
  }, [])

  const labelIcon = useCallback((index: number, length: number) => {
    if (length === 1) {
      return "startLocation"
    } else if (index === 0) {
      return "startLocation"
    } else if (index === length - 1) {
      return "finalLocation"
    } else {
      return "markerDot"
    }
  }, [])

  return (
    <>
      {markers?.map(({ lat, lng, label }, i) => (
        <Marker
          key={i}
          position={[lat, lng]}
          icon={active === i ? markerIconActive(i, markers.length) : markerIcon(i, markers.length)}
          eventHandlers={{
            click: () => {
              if (i === active) {
                setActive(-1)
                setActiveLabel(null)
              } else {
                setActive(i)
                setActiveLabel(label)
                setLabelIcon(labelIcon(i, markers.length))
              }
            },
          }}
        />
      ))}
    </>
  )
}

const MapCurve: FC<{ markers: MapOverviewMarker[] }> = ({ markers }) => {
  const map = useMap()
  const theme = useTheme()

  const curveData: (string | number[])[] = []
  markers.forEach(({ lat, lng }, i) => {
    if (i === 0) {
      curveData.push("M")
    } else if (i === 1) {
      curveData.push("L")
    }
    curveData.push([lat, lng])
  })
  const curve: L.Spline = spline(
    markers.map(({ lat, lng }) => [lat, lng]),
    {
      color: theme.palette["primary.800"].main,
      weight: 1.5,
      smoothing: 0.15,
    }
  )

  curve.addTo(map)

  return <></>
}

// A sub component for the map legend
const MapLegend: FC = () => {
  return (
    <Box display="flex" alignItems="center" gap={{ xs: 0, md: 2 }} height={50}>
      <Typography variant="body.sm" sx={(theme) => ({ color: theme.palette["neutral.700"].main })}>
        Travel Itinerary
      </Typography>
      <Stack direction="row" alignItems="center" columnGap={2} padding={3}>
        <SvgIcon
          variant="tripStart"
          size={12}
          sx={(theme) => ({ fill: theme.palette["neutral.300"].main })}
          viewBox="0 0 10 14"
        />
        <Typography variant="body.sm">Trip Starts</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" columnGap={2} padding={3}>
        <Box
          sx={(theme) => ({
            width: 10,
            height: 10,
            borderRadius: "100px",
            bgcolor: theme.palette["neutral.300"].main,
          })}
        />
        <Typography variant="body.sm">Trip Stop</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" columnGap={2} padding={3}>
        <SvgIcon
          variant="tripEnd"
          sx={(theme) => ({ fill: theme.palette["neutral.300"].main })}
          viewBox="0 0 12 14"
          size={12}
        />
        <Typography variant="body.sm">Trip Ends</Typography>
      </Stack>
    </Box>
  )
}
